/* eslint-disable no-param-reassign */
import { CreditRating, PurchaseTermNames, LeaseMileageNames, FinanceType, CreditRatingMax, LeaseTermNames } from './constants';
import { CREDIT_RATING_MAX } from './legos/constants/creditPricing';
import { CreditPricing, CreditTier, Pair, Tier } from './legos/creditPricingType';
import { AprConfig, CreditTiers, PurchaseTerms, PurchaseTermsAPRMarkup } from './legos/dealerTypes';

/**
 * Attach isDefault value to transformed rangeTiers.
 *
 * If there's no default tier is set for given tiers, it will set isDefault to true for the first tier.
 */
const assignIsDefaultToRangeTiers = (
  rangeTiers: Tier[],
  creditTiers: CreditTier[],
): Tier[] => {
  let defaultTierCount = 0;

  rangeTiers.forEach((tier, index) => {
    tier.isDefault = creditTiers[index].isDefault;
    tier.displayName = creditTiers[index].displayName || '';
    if (creditTiers[index].isDefault) {
      defaultTierCount += 1;
    }
  });

  if (defaultTierCount === 0) {
    rangeTiers[0].isDefault = true;
  }

  return rangeTiers;
};

export const transformToRangeTierType = (
  creditTiers?: CreditTier[],
): Tier[] => {
  if (!creditTiers || creditTiers.length === 0) return [];

  const initialValue: Pair<number, number>[] = [
    [creditTiers[0].value, CREDIT_RATING_MAX],
  ];

  const rangeTiers = creditTiers
    .slice(1)
    .reduce(
      (accum, { value }) => [
        ...accum,
        [value, accum[accum.length - 1][0] - 1] as Pair<number, number>,
      ],
      initialValue,
    )
    .map(([x, y]): Tier => ({ low: x.toString(), high: y.toString() }));
  assignIsDefaultToRangeTiers(rangeTiers, creditTiers);
  return rangeTiers;
};

const ORDERED_CREDIT_RATING = [
  CreditRating.EXCELLENT,
  CreditRating.GOOD,
  CreditRating.AVERAGE,
  CreditRating.POOR,
];
const ORDERED_TERM = [
  PurchaseTermNames.TERM1,
  PurchaseTermNames.TERM2,
  PurchaseTermNames.TERM3,
];
const ORDERED_MILEAGES = [
  LeaseMileageNames.MILEAGE1,
  LeaseMileageNames.MILEAGE2,
  LeaseMileageNames.MILEAGE3,
  LeaseMileageNames.MILEAGE4,
  LeaseMileageNames.MILEAGE5,
];

type LeaseMileages = {
  [index in LeaseMileageNames]?: number;
};

export function convertNewCreditPricingToOld(
  creditPricing: CreditPricing,
): AprConfig {
  // use finance tiers as base
  const { creditTiers } = creditPricing.creditPricing[FinanceType.PURCHASE];
  const mappedTiers = ORDERED_CREDIT_RATING.reduce((acc, rating, idx) => {
    const high = idx === 0 ? CreditRatingMax : creditTiers[idx - 1].value - 1;
    acc[rating] = {
      high,
      low: creditTiers[idx].value,
    };
    return acc;
  }, {} as CreditTiers);

  const numberLeaseMileages = new Set(
    creditPricing.creditPricing[FinanceType.LEASE].terms.map(t => t.mileage),
  ).size;

  const purchaseTerms = ORDERED_TERM.reduce((acc, termKey, idx) => {
    if (idx <= 2) {
      acc[termKey] =
        creditPricing.creditPricing[FinanceType.PURCHASE].terms[idx].months;
    }
    return acc;
  }, {} as PurchaseTerms);

  const purchaseCredit = ORDERED_CREDIT_RATING.reduce(
    (acc, rating, idx) => {
      const pricing = ORDERED_TERM.reduce(
        (pricingAcc, term, termIdx) => {
          const newPricing =
            creditPricing.creditPricing[FinanceType.PURCHASE].pricing[idx][
              termIdx
            ];
          // eslint-disable-next-line no-param-reassign
          pricingAcc.apr[term] = newPricing.apr;
          // eslint-disable-next-line no-param-reassign
          pricingAcc.markup[term] = newPricing.markup;
          return pricingAcc;
        },
        { apr: {}, markup: {} } as {
          apr: PurchaseTerms;
          markup: PurchaseTermsAPRMarkup;
        },
      );

      acc[rating] = pricing;
      return acc;
    },
    {} as {
      [index in CreditRating]: {
        apr?: PurchaseTerms;
        markup?: PurchaseTermsAPRMarkup;
      };
    },
  );

  const leaseMileages = ORDERED_MILEAGES.reduce((acc, mileage, idx) => {
    if (idx < numberLeaseMileages) {
      acc[mileage] =
        creditPricing.creditPricing[FinanceType.LEASE].terms[idx].mileage;
    }
    return acc;
  }, {} as LeaseMileages);

  const leaseTerms = Object.values(LeaseTermNames).reduce(
    (acc, termName, termIdx) => {
      const leaseConfig = creditPricing.creditPricing[FinanceType.LEASE];
      const term = leaseConfig.terms[termIdx * numberLeaseMileages]?.months;
      if (term) {
        const credit = ORDERED_CREDIT_RATING.reduce(
          (creditAcc, rating, creditIdx) => {
            const pricing = leaseConfig.pricing[creditIdx] || [];

            // eslint-disable-next-line no-param-reassign
            creditAcc[rating] = ORDERED_MILEAGES.reduce(
              (mileageAcc, mileage, idx) => {
                if (idx < numberLeaseMileages) {
                  // eslint-disable-next-line no-param-reassign
                  mileageAcc.moneyFactor[mileage] =
                    pricing[termIdx * numberLeaseMileages + idx].moneyFactor;
                  // eslint-disable-next-line no-param-reassign
                  mileageAcc.onePayLeaseMoneyFactor[mileage] =
                    pricing[
                      termIdx * numberLeaseMileages + idx
                    ].onePayLeaseMoneyFactor;
                }
                return mileageAcc;
              },
              { moneyFactor: {}, onePayLeaseMoneyFactor: {} } as {
                moneyFactor: LeaseMileages;
                onePayLeaseMoneyFactor: LeaseMileages;
              },
            );
            return creditAcc;
          },
          {} as {
            [index in CreditRating]: {
              moneyFactor: LeaseMileages;
              onePayLeaseMoneyFactor: LeaseMileages;
            };
          },
        );

        acc[termName] = {
          term,
          credit,
        };
      }
      return acc;
    },
    {} as {
      [index in LeaseTermNames]?: {
        term?: number;
        credit?: {
          [index in CreditRating]?: {
            moneyFactor?: LeaseMileages;
            onePayLeaseMoneyFactor?: LeaseMileages;
          };
        };
      };
    },
  );

  return {
    enabled: true,
    creditTiers: mappedTiers,
    purchase: {
      terms: purchaseTerms,
      credit: purchaseCredit,
    },
    lease: {
      mileages: leaseMileages,
      terms: leaseTerms,
    },
    options: {
      addOEMMarkupRate: creditPricing.creditPricing[
        FinanceType.PURCHASE
      ].pricing.some(tierPricing => tierPricing.some(p => !!p.markup)),
      useManufacturerRebate: creditPricing.options.useManufacturerRebate,
      useManufacturerApr: ORDERED_CREDIT_RATING.reduce(
        (acc, rating, idx) => {
          const tierKey = creditTiers[idx].key;
          acc[rating] = !!creditPricing.options.useManufacturerApr?.[tierKey];
          return acc;
        },
        { purchase: !!creditPricing.options.useManufacturerApr } as {
          [key in CreditRating]?: boolean;
        } & { purchase?: boolean },
      ),
    },
  };
}
